import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFmt } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
    FileText, CheckSquare, ClipboardList, ScanLine, TrendingUp, Plus, ArrowRight, Loader2,
} from "lucide-react";

const STAT = [
    { key: "purchase_requests", label: "Purchase Requests", icon: FileText },
    { key: "pending_approvals", label: "Pending Approvals", icon: CheckSquare },
    { key: "purchase_orders", label: "Purchase Orders", icon: ClipboardList },
    { key: "posted_invoices", label: "Posted to Veracross", icon: ScanLine },
];

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data)
        return <div className="grid place-items-center py-20 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>;

    return (
        <div className="space-y-6 pe-fade-up" data-testid="dashboard">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Welcome, {user.name?.split(" ")[0]}</h1>
                    <p className="text-muted-foreground text-sm mt-1">Here's what's happening across purchasing today.</p>
                </div>
                {["tenant_admin", "requester", "approver", "ap_clerk"].includes(user.role) && (
                    <Link to="/app/requests/new">
                        <Button className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="dashboard-new-request">
                            <Plus className="h-4 w-4 mr-1" /> New Request
                        </Button>
                    </Link>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {STAT.map((s) => {
                    const Icon = s.icon;
                    return (
                        <div key={s.key} className="bg-white border border-border rounded-xl p-5" data-testid={`stat-${s.key}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">{s.label}</span>
                                <Icon className="h-4 w-4 text-brand-teal" />
                            </div>
                            <div className="mt-3 text-3xl font-semibold font-display text-brand-emerald">{data.counts[s.key]}</div>
                        </div>
                    );
                })}
            </div>

            {/* Committed spend */}
            <div className="bg-emerald-50 border border-emerald-100 text-brand-emerald rounded-xl p-6 flex items-center justify-between">
                <div>
                    <div className="text-sm text-slate-600 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Total Committed (POs)</div>
                    <div className="mt-2 text-4xl font-semibold font-display">{money(data.total_committed)}</div>
                </div>
                <Link to="/app/orders" className="text-brand-teal hover:text-brand-emerald text-sm inline-flex items-center gap-1">
                    View orders <ArrowRight className="h-4 w-4" />
                </Link>
            </div>

            {/* Recent */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white border border-border rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                        <h3 className="font-semibold text-brand-emerald">Recent Requests</h3>
                        <Link to="/app/requests" className="text-xs text-brand-teal hover:underline">View all</Link>
                    </div>
                    <div className="divide-y divide-border">
                        {data.recent_requests.length === 0 && <div className="px-5 py-8 text-sm text-muted-foreground text-center">No requests yet.</div>}
                        {data.recent_requests.map((r) => (
                            <Link to={`/app/requests/${r.id}`} key={r.id} className="flex items-center justify-between px-5 py-3 hover:bg-secondary/60" data-testid={`recent-request-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="font-mono text-xs text-muted-foreground">{r.pr_number}</div>
                                    <div className="text-sm truncate">{r.vendor_name || "—"} · {money(r.total)}</div>
                                </div>
                                <StatusBadge status={r.status} />
                            </Link>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-border rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                        <h3 className="font-semibold text-brand-emerald">Recent Invoices</h3>
                        {["tenant_admin", "ap_clerk"].includes(user.role) && <Link to="/app/invoices" className="text-xs text-brand-teal hover:underline">View all</Link>}
                    </div>
                    <div className="divide-y divide-border">
                        {data.recent_invoices.length === 0 && <div className="px-5 py-8 text-sm text-muted-foreground text-center">No invoices yet.</div>}
                        {data.recent_invoices.map((r) => (
                            <div key={r.id} className="flex items-center justify-between px-5 py-3">
                                <div className="min-w-0">
                                    <div className="font-mono text-xs text-muted-foreground">{r.invoice_number || r.original_filename}</div>
                                    <div className="text-sm truncate">{r.vendor_name || "—"} · {money(r.total)}</div>
                                </div>
                                <StatusBadge status={r.status} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
